import { useState, useCallback, useEffect } from "react";
import { createFileRoute, Link, Outlet, useMatch, useNavigate } from "@tanstack/react-router";
import {
  RefreshCw,
  Trash2,
  Eye,
  AlertCircle,
  Loader2,
  MoreHorizontal,
  FileText,
  Plus,
  ShieldCheck,
  FolderTree,
  Tag,
  X,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import { SearchInput } from "@/components/ui/search-input";
import { Pagination } from "@/components/ui/pagination";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDateTime } from "@/lib/dateFormat";
import { useAuth } from "@/contexts/AuthContext";
import { usePortalData } from "@/components/portal/PortalDataProvider";
import { usePermissions } from "@/hooks/usePermissions";
import { ObjectGroupAccessDialog } from "@/components/portal/groups/ObjectGroupAccessDialog";
import { useGroupAccessDeepLink } from "@/components/portal/groups/useGroupAccessDeepLink";
import { BulkGroupAccessDialog } from "@/components/portal/groups/BulkGroupAccessDialog";
import { useObjectPermissions } from "@/hooks/useObjectPermissions";
import { groupsApi } from "@/services/api/groups";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  usePaginatedData,
  type PaginatedResponse,
} from "@/hooks/usePaginatedData";
import { PermissionDenied } from "@/components/ui/permission-denied";
import { ragApi, type DocumentSummary } from "@/services/api/rag";
import { platformApi, type Category, type DocumentLabel } from "@/services/api/platform";

export const Route = createFileRoute("/portal/knowledgebase-file/documents")({
  component: DocumentsLayout,
  validateSearch: (
    search: Record<string, unknown>,
  ): { groupAccess?: string; groupAccessName?: string } => ({
    groupAccess:
      typeof search.groupAccess === "string" && search.groupAccess.length > 0
        ? search.groupAccess
        : undefined,
    groupAccessName:
      typeof search.groupAccessName === "string" && search.groupAccessName.length > 0
        ? search.groupAccessName
        : undefined,
  }),
});

function DocumentsLayout() {
  const childMatch = useMatch({
    from: "/portal/knowledgebase-file/documents/$id",
    shouldThrow: false,
  });

  if (childMatch) {
    return <Outlet />;
  }

  return <DocumentsListPage />;
}

function DocumentsListPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { selectedOrganisation } = usePortalData();
  const { can, isAdmin, bypassesGroups } = usePermissions();
  const canEditDocuments = can("edit", "kb-file");
  const canDeleteDocuments = can("delete", "kb-file");
  const canManageGroups = can("edit", "groups") || isAdmin;

  const organisationId = selectedOrganisation?.organisationId || "";

  const { canEdit: objCanEdit, canDelete: objCanDelete } = useObjectPermissions(
    token ? () => ragApi.getMyPermissions(token) : null,
    [token, organisationId],
  );

  // Search, filter & sort state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [labelFilter, setLabelFilter] = useState<string>("all");
  const [sortBy, setSortBy] = useState<string>("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Filter dropdown options
  const [filterCategories, setFilterCategories] = useState<Category[]>([]);
  const [filterLabels, setFilterLabels] = useState<DocumentLabel[]>([]);

  // Load filter dropdown options
  useEffect(() => {
    if (!token || !organisationId) return;
    platformApi
      .listCategories(token, { organisation_id: organisationId, limit: 200 })
      .then((res) => setFilterCategories(res.items))
      .catch(() => {});
    platformApi
      .listLabels(token, { organisation_id: organisationId })
      .then((res) => setFilterLabels(res))
      .catch(() => {});
  }, [token, organisationId]);

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("desc");
    }
  };

  const getSortDirection = (column: string): "asc" | "desc" | null => {
    return sortBy === column ? sortOrder : null;
  };

  // Dialog state
  const [selectedDocument, setSelectedDocument] =
    useState<DocumentSummary | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [groupAccessTarget, setGroupAccessTarget] = useState<{ id: string; name: string } | null>(null);
  const [bulkEnriching, setBulkEnriching] = useState(false);
  const [bulkReprocessing, setBulkReprocessing] = useState(false);
  const [showBulkReprocessDialog, setShowBulkReprocessDialog] = useState(false);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
  const [assigning, setAssigning] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const [categoryPage, setCategoryPage] = useState(1);
  const [categoryResults, setCategoryResults] = useState<{ items: Category[]; total: number }>({ items: [], total: 0 });
  const [categoryLoading, setCategoryLoading] = useState(false);
  const CATEGORY_PAGE_SIZE = 20;

  // Label assignment state
  const [showLabelDialog, setShowLabelDialog] = useState(false);
  const [selectedLabelIds, setSelectedLabelIds] = useState<Set<string>>(new Set());
  const [assigningLabels, setAssigningLabels] = useState(false);
  const [availableLabels, setAvailableLabels] = useState<DocumentLabel[]>([]);
  const [labelsLoading, setLabelsLoading] = useState(false);

  // Group permissions per document (fetched in bulk)
  const [docPermissions, setDocPermissions] = useState<
    Map<string, { group_name: string; role: string }[]>
  >(new Map());
  const [bulkGroupAccessOpen, setBulkGroupAccessOpen] = useState(false);

  // Category name cache: { categoryId -> Category }
  const [categoryCache, setCategoryCache] = useState<Record<string, Category>>({});

  // Label assignments per document: { docId -> labels[] }
  const [docLabels, setDocLabels] = useState<
    Record<string, DocumentLabel[]>
  >({});

  // Load labels when dialog opens
  useEffect(() => {
    if (!showLabelDialog || !token || !organisationId) return;
    setLabelsLoading(true);
    platformApi
      .listLabels(token, { organisation_id: organisationId })
      .then((res) => setAvailableLabels(res))
      .catch(() => toast.error("Failed to load labels"))
      .finally(() => setLabelsLoading(false));
  }, [showLabelDialog, token, organisationId]);

  // Load categories when dialog opens, search changes, or page changes
  useEffect(() => {
    if (!showCategoryDialog || !token) return;
    setCategoryLoading(true);
    const offset = (categoryPage - 1) * CATEGORY_PAGE_SIZE;
    platformApi
      .listCategories(token, {
        search: categorySearch || undefined,
        limit: CATEGORY_PAGE_SIZE,
        offset,
        organisation_id: organisationId || undefined,
      })
      .then((res) => setCategoryResults({ items: res.items, total: res.total }))
      .catch(() => toast.error("Failed to load categories"))
      .finally(() => setCategoryLoading(false));
  }, [showCategoryDialog, token, categorySearch, categoryPage, organisationId]);

  // Reset page when search changes
  useEffect(() => {
    setCategoryPage(1);
  }, [categorySearch]);

  // Clear selection when page/filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchTerm, statusFilter, categoryFilter, labelFilter, sortBy, sortOrder]);

  // Server-side pagination
  const fetchDocuments = useCallback(
    async (
      page: number,
      pageSize: number,
    ): Promise<PaginatedResponse<DocumentSummary>> => {
      if (!token || !organisationId)
        return { data: [], totalCount: 0, page, pageSize };
      const response = await ragApi.listDocuments(token, {
        search: searchTerm || undefined,
        category_id: categoryFilter !== "all" ? categoryFilter : undefined,
        label_ids: labelFilter !== "all" ? [labelFilter] : undefined,
        skip: (page - 1) * pageSize,
        limit: pageSize,
        sort_by: sortBy as "created_at" | "total_chunks" | "file_name" | "processing_status" | "total_embeddings",
        sort_order: sortOrder,
        status:
          statusFilter !== "all"
            ? (statusFilter as "indexed" | "processing" | "failed")
            : undefined,
      });
      return {
        data: response.documents,
        totalCount: response.total,
        page,
        pageSize,
      };
    },
    [token, organisationId, statusFilter, categoryFilter, labelFilter, searchTerm, sortBy, sortOrder],
  );

  const {
    data: documents,
    isLoading,
    error: loadError,
    isPermissionDenied: accessDenied,
    pagination,
    pageSizeOptions,
    goToPage,
    nextPage,
    prevPage,
    firstPage,
    lastPage,
    setPageSize,
    refresh,
    canGoNext,
    canGoPrev,
    startIndex,
    endIndex,
  } = usePaginatedData<DocumentSummary>({
    fetchFn: fetchDocuments,
    dependencies: [token, organisationId, statusFilter, categoryFilter, labelFilter, searchTerm, sortBy, sortOrder],
  });

  // Deep link from the group Object Permissions tab: ?groupAccess=<document id>
  const { groupAccess, groupAccessName } = Route.useSearch();
  useGroupAccessDeepLink({
    groupAccess,
    groupAccessName,
    // Server-paginated at 25, so this is normally false and the link's own name is used.
    // `totalCount` is narrowed by the search and filters, so "page holds everything" only
    // means "everything MATCHING". Normally the link is consumed on first load with all
    // filters at their defaults, but a failed first fetch defers consumption — and this
    // page renders no error UI, so the user can search during that window and a 3-of-3
    // result would otherwise read as a complete list and report a false not-found.
    listIsComplete:
      documents.length >= pagination.totalCount &&
      !searchTerm &&
      statusFilter === "all" &&
      categoryFilter === "all" &&
      labelFilter === "all" &&
      // See the note on the other hosts: a non-bypass caller's list is server-filtered,
      // so "complete" would still omit objects that exist.
      bypassesGroups,
    // `!loadError` matters: a failed fetch leaves `documents` empty and `totalCount` at 0,
    // which would read as a complete empty list and report a false "Object not found".
    ready: !isLoading && !loadError && !!organisationId,
    canManageGroups,
    items: documents,
    getId: (doc) => doc.document_id,
    getName: (doc) => doc.file_name,
    onMatch: setGroupAccessTarget,
  });

  // Load category names for visible documents (using category_id from RAG response)
  useEffect(() => {
    if (!token || documents.length === 0) return;
    const missingIds = documents
      .flatMap((d) =>
        // Mirror the cell's render logic: prefer the many-to-many assignments,
        // fall back to the legacy single category_id (also covers the window
        // where the FE has deployed but the BE hasn't yet sent category_ids).
        d.category_ids && d.category_ids.length > 0
          ? d.category_ids
          : d.category_id
            ? [d.category_id]
            : [],
      )
      .filter((id): id is string => !!id && !categoryCache[id]);
    const uniqueIds = [...new Set(missingIds)];
    if (uniqueIds.length === 0) return;

    const loadCategories = async () => {
      try {
        const res = await platformApi.listCategories(token, {
          organisation_id: organisationId || undefined,
          limit: 200,
        });
        const cache: Record<string, Category> = { ...categoryCache };
        for (const cat of res.items) {
          cache[cat.id] = cat;
        }
        setCategoryCache(cache);
      } catch {
        // Categories may not be available
      }
    };
    loadCategories();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- categoryCache is written by this effect; adding it would cause an infinite loop
  }, [token, organisationId, documents]);

  // Load labels for visible documents from CP
  useEffect(() => {
    if (!token || !organisationId || documents.length === 0) return;
    const loadLabels = async () => {
      const results: Record<string, DocumentLabel[]> = {};
      await Promise.all(
        documents.map(async (doc) => {
          try {
            const data = await platformApi.getDocumentWithLabels(
              token,
              doc.document_id,
              organisationId,
            );
            if (data.labels && data.labels.length > 0) {
              results[doc.document_id] = data.labels;
            }
          } catch {
            // Document may not have labels or endpoint may not be available
          }
        }),
      );
      setDocLabels(results);
    };
    loadLabels();
  }, [token, organisationId, documents]);

  // Load group permissions for all documents (bulk)
  const fetchDocPermissions = useCallback(async () => {
    // cp answers by-object only for org_owner / org_admin / platform_admin (BYPASS_ROLES,
    // `_require_grant_visibility`); anyone else gets a 403 that trips Platform Health (web #1444).
    if (!token || !organisationId || !bypassesGroups) return;
    try {
      const resp = await groupsApi.listObjectPermissionsByObject(token, organisationId, {
        service_name: "rag",
        object_type: "document",
      });
      const grouped = new Map<string, { group_name: string; role: string }[]>();
      for (const item of resp.items) {
        const existing = grouped.get(item.object_id) ?? [];
        existing.push({ group_name: item.group_name, role: item.role });
        grouped.set(item.object_id, existing);
      }
      setDocPermissions(grouped);
    } catch {
      // Non-critical — page still works without group badges
    }
  }, [token, organisationId, bypassesGroups]);

  useEffect(() => {
    fetchDocPermissions();
  }, [fetchDocPermissions]);

  // Selection helpers
  const allOnPageSelected =
    documents.length > 0 &&
    documents.every((d) => selectedIds.has(d.document_id));
  const someOnPageSelected =
    documents.some((d) => selectedIds.has(d.document_id)) &&
    !allOnPageSelected;

  const toggleSelectAll = () => {
    if (allOnPageSelected) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        documents.forEach((d) => next.delete(d.document_id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        documents.forEach((d) => next.add(d.document_id));
        return next;
      });
    }
  };

  const toggleSelect = (docId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(docId)) {
        next.delete(docId);
      } else {
        next.add(docId);
      }
      return next;
    });
  };

  const toggleCategorySelection = (catId: string) => {
    setSelectedCategoryIds((prev) => {
      const next = new Set(prev);
      if (next.has(catId)) {
        next.delete(catId);
      } else {
        next.add(catId);
      }
      return next;
    });
  };

  const toggleLabelSelection = (labelId: string) => {
    setSelectedLabelIds((prev) => {
      const next = new Set(prev);
      if (next.has(labelId)) {
        next.delete(labelId);
      } else {
        next.add(labelId);
      }
      return next;
    });
  };

  const handleBulkAssignLabels = async () => {
    if (!token || !organisationId || selectedLabelIds.size === 0) return;
    setAssigningLabels(true);
    const docIds = Array.from(selectedIds);
    const labelIds = Array.from(selectedLabelIds);
    let successCount = 0;
    let errorCount = 0;

    for (const docId of docIds) {
      try {
        await platformApi.updateDocumentLabels(token, docId, organisationId, labelIds);
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setAssigningLabels(false);
    setShowLabelDialog(false);
    setSelectedLabelIds(new Set());

    if (errorCount === 0) {
      toast.success(
        `Assigned ${labelIds.length} label${labelIds.length !== 1 ? "s" : ""} to ${docIds.length} document${docIds.length !== 1 ? "s" : ""}`,
      );
    } else {
      toast.warning(`${successCount} assigned, ${errorCount} failed`);
    }
    setSelectedIds(new Set());
    refresh();
  };

  const handleBulkAssignCategory = async () => {
    if (!token || !organisationId || selectedCategoryIds.size === 0) return;
    setAssigning(true);
    const docIds = Array.from(selectedIds);
    const catIds = Array.from(selectedCategoryIds);
    let successCount = 0;
    let errorCount = 0;

    for (const docId of docIds) {
      for (const catId of catIds) {
        try {
          await platformApi.assignCategoryToDocument(token, docId, {
            document_id: docId,
            category_id: catId,
            organisation_id: organisationId,
            assigned_by: "user",
            confidence: 1.0,
          });
          successCount++;
        } catch {
          errorCount++;
        }
      }
    }

    setAssigning(false);
    setShowCategoryDialog(false);
    setSelectedCategoryIds(new Set());

    if (errorCount === 0) {
      toast.success(`Assigned ${catIds.length} categor${catIds.length !== 1 ? "ies" : "y"} to ${docIds.length} document${docIds.length !== 1 ? "s" : ""}`);
    } else {
      toast.warning(
        `${successCount} assigned, ${errorCount} failed`,
      );
    }
    setSelectedIds(new Set());
    refresh();
  };

  const handleDelete = async () => {
    if (!selectedDocument || !token) return;
    setDeleting(true);
    try {
      const response = await ragApi.deleteDocument(
        token,
        selectedDocument.document_id,
      );
      if (response.errors && response.errors.length > 0) {
        toast.warning("Partial deletion", {
          description: `Document deleted with warnings: ${response.errors.join(", ")}`,
        });
      } else {
        toast.success("Document deleted", {
          description: `${selectedDocument.file_name} has been removed`,
        });
      }
      setShowDeleteDialog(false);
      setSelectedDocument(null);
      refresh();
    } catch (error) {
      console.error("Failed to delete document:", error);
      toast.error("Delete failed", {
        description: "Could not delete document",
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleReprocess = async () => {
    if (!selectedDocument || !token) return;
    setReprocessing(true);
    try {
      await ragApi.reprocessDocument(
        token,
        selectedDocument.document_id,
      );
      setShowReprocessDialog(false);
      setSelectedDocument(null);
      toast.success("Reprocessing started", {
        description: `${selectedDocument.file_name} is being reprocessed.`,
      });
      navigate({ to: "/portal/knowledgebase-file/batches" });
    } catch (error) {
      console.error("Failed to reprocess document:", error);
      toast.error("Reprocess failed", {
        description: "Could not reprocess document",
      });
    } finally {
      setReprocessing(false);
    }
  };

  const handleEnrich = async (doc: DocumentSummary) => {
    if (!token) return;
    setEnriching(true);
    try {
      await ragApi.enrichDocument(token, doc.document_id, {});
      toast.success("Enrichment started", {
        description: `${doc.file_name} is being enriched with categories and labels.`,
      });
      refresh();
    } catch (error) {
      console.error("Failed to enrich document:", error);
      toast.error("Enrich failed", {
        description: "Could not start enrichment",
      });
    } finally {
      setEnriching(false);
    }
  };

  const handleBulkEnrich = async () => {
    if (!token) return;
    setBulkEnriching(true);
    const docIds = Array.from(selectedIds);
    let successCount = 0;
    let errorCount = 0;

    for (const docId of docIds) {
      try {
        await ragApi.enrichDocument(token, docId, {});
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setBulkEnriching(false);

    if (errorCount === 0) {
      toast.success(
        `Enrichment started for ${docIds.length} document${docIds.length !== 1 ? "s" : ""}`,
      );
    } else {
      toast.warning(`${successCount} started, ${errorCount} failed`);
    }
    setSelectedIds(new Set());
    refresh();
  };

  const handleBulkReprocess = async () => {
    if (!token) return;
    setBulkReprocessing(true);
    setShowBulkReprocessDialog(false);
    const docIds = Array.from(selectedIds);
    let successCount = 0;
    let errorCount = 0;

    for (const docId of docIds) {
      try {
        await ragApi.reprocessDocument(token, docId);
        successCount++;
      } catch {
        errorCount++;
      }
    }

    setBulkReprocessing(false);

    setSelectedIds(new Set());

    if (errorCount === 0) {
      toast.success(
        `Reprocessing started for ${docIds.length} document${docIds.length !== 1 ? "s" : ""}`,
      );
      navigate({ to: "/portal/knowledgebase-file/batches" });
    } else {
      toast.warning(`${successCount} reprocessing, ${errorCount} failed`);
      refresh();
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "indexed":
        return (
          <Badge variant="success">
            Indexed
          </Badge>
        );
      case "processing":
        return (
          <Badge variant="default">
            Processing
          </Badge>
        );
      case "failed":
        return (
          <Badge variant="destructive">
            Failed
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (accessDenied) {
    return (
      <PermissionDenied
        message="You don't have permission to access these documents. Ask your administrator to grant you access."
        backTo="/portal/knowledgebase-file"
        backLabel="Back to Knowledgebase File"
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl md:text-4xl font-bold text-slate-900">Documents</h1>
          <p className="mt-1 md:mt-2 text-sm md:text-lg text-slate-600">
            Browse and manage indexed documents
          </p>
        </div>
        <Link to="/portal/knowledgebase-file/upload">
          <Button className="rounded-full">
            <Plus className="h-4 w-4 mr-2" />
            Upload
          </Button>
        </Link>
      </div>

      {/* Content card */}
      <div className="rounded-3xl border border-slate-100 bg-white p-3 md:p-6 shadow-sm space-y-4 md:space-y-6">
        {/* Controls bar */}
        <div className="flex flex-col gap-2 md:flex-row md:flex-wrap md:items-center md:gap-3">
          <SearchInput
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onClear={() => setSearchTerm("")}
            placeholder="Search by file name..."
            containerClassName="flex-1 min-w-[200px]"
          />
          <Select
            value={statusFilter}
            onValueChange={(value) => setStatusFilter(value)}
          >
            <SelectTrigger className="w-full md:w-[140px]">
              <SelectValue placeholder="All Statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="indexed">Indexed</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
          {filterCategories.length > 0 && (
            <Select
              value={categoryFilter}
              onValueChange={(value) => setCategoryFilter(value)}
            >
              <SelectTrigger className="w-full md:w-[160px]">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {filterCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {filterLabels.length > 0 && (
            <Select
              value={labelFilter}
              onValueChange={(value) => setLabelFilter(value)}
            >
              <SelectTrigger className="w-full md:w-[140px]">
                <SelectValue placeholder="All Labels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Labels</SelectItem>
                {filterLabels.map((label) => (
                  <SelectItem key={label.id} value={label.id}>
                    {label.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button variant="outline" size="sm" onClick={refresh}>
            {isLoading ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {/* Bulk action bar */}
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 md:gap-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
            <span className="text-sm font-medium">
              {selectedIds.size} document{selectedIds.size !== 1 ? "s" : ""} selected
            </span>
            {canEditDocuments && (
              <Button
                size="sm"
                onClick={() => setShowCategoryDialog(true)}
              >
                <FolderTree className="h-4 w-4 mr-2" />
                Assign Category
              </Button>
            )}
            {canEditDocuments && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowLabelDialog(true)}
              >
                <Tag className="h-4 w-4 mr-2" />
                Assign Label
              </Button>
            )}
            {canEditDocuments && (
              <Button
                size="sm"
                variant="outline"
                onClick={handleBulkEnrich}
                disabled={bulkEnriching}
                title="Enrich documents with AI-generated categories and labels"
              >
                {bulkEnriching ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-2" />
                )}
                Enrich
              </Button>
            )}
            {canEditDocuments && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setShowBulkReprocessDialog(true)}
                disabled={bulkReprocessing}
                title="Re-extract and re-embed selected documents"
              >
                {bulkReprocessing ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Reprocess
              </Button>
            )}
            {canManageGroups && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setBulkGroupAccessOpen(true)}
              >
                <ShieldCheck className="h-4 w-4 mr-2" />
                Group Access
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedIds(new Set())}
            >
              <X className="h-4 w-4 mr-1" />
              Clear
            </Button>
          </div>
        )}

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : documents.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">
              {searchTerm || statusFilter !== "all" || categoryFilter !== "all" || labelFilter !== "all"
                ? "No documents match your filters"
                : "No documents yet"}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm || statusFilter !== "all" || categoryFilter !== "all" || labelFilter !== "all"
                ? "Try adjusting your filters."
                : "Upload documents to build your knowledgebase."}
            </p>
            {!searchTerm && statusFilter === "all" && categoryFilter === "all" && labelFilter === "all" && (
              <Link to="/portal/knowledgebase-file/upload">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Upload Documents
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]">
                  <Checkbox
                    checked={
                      allOnPageSelected
                        ? true
                        : someOnPageSelected
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={toggleSelectAll}
                    aria-label="Select all documents on this page"
                  />
                </TableHead>
                <SortableTableHead
                  direction={getSortDirection("file_name")}
                  onSort={() => handleSort("file_name")}
                >
                  File Name
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("processing_status")}
                  onSort={() => handleSort("processing_status")}
                >
                  Status
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("total_chunks")}
                  onSort={() => handleSort("total_chunks")}
                  className="hidden md:table-cell"
                >
                  Chunks
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("total_embeddings")}
                  onSort={() => handleSort("total_embeddings")}
                  className="hidden lg:table-cell"
                >
                  Embeddings
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="hidden md:table-cell"
                >
                  Categories
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="hidden lg:table-cell"
                >
                  Labels
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="hidden lg:table-cell"
                >
                  Groups
                </SortableTableHead>
                <SortableTableHead
                  direction={getSortDirection("created_at")}
                  onSort={() => handleSort("created_at")}
                  className="hidden md:table-cell"
                >
                  Created
                </SortableTableHead>
                <SortableTableHead
                  direction={null}
                  onSort={() => {}}
                  disabled
                  className="w-[50px]"
                >
                  {" "}
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((document) => (
                <TableRow
                  key={document.document_id}
                  className={
                    selectedIds.has(document.document_id) ? "bg-primary/5" : ""
                  }
                >
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(document.document_id)}
                      onCheckedChange={() =>
                        toggleSelect(document.document_id)
                      }
                      aria-label={`Select ${document.file_name}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium max-w-xs truncate" title={document.file_name}>
                    {document.file_name}
                  </TableCell>
                  <TableCell>
                    {getStatusBadge(document.processing_status)}
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <Badge variant="outline">{document.total_chunks}</Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <Badge variant="outline">
                      {document.total_embeddings}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(document.category_ids && document.category_ids.length > 0
                        ? document.category_ids
                        : document.category_id
                          ? [document.category_id]
                          : []
                      ).map((catId) => (
                        <Badge key={catId} variant="secondary" className="text-xs">
                          {categoryCache[catId]?.name ?? "…"}
                        </Badge>
                      ))}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {(docLabels[document.document_id] || []).map(
                        (label) => (
                          <Badge
                            key={label.id}
                            variant="outline"
                            className="text-xs gap-1"
                          >
                            {label.color && (
                              <div
                                className="w-2 h-2 rounded-full"
                                style={{ backgroundColor: label.color }}
                              />
                            )}
                            {label.name}
                          </Badge>
                        ),
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell">
                    <GroupBadges permissions={docPermissions.get(document.document_id)} />
                  </TableCell>
                  <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                    {formatDateTime(document.created_at)}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() =>
                            navigate({
                              to: "/portal/knowledgebase-file/documents/$id",
                              params: { id: document.document_id },
                            })
                          }
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {canEditDocuments && objCanEdit(document.document_id) && (
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedDocument(
                                document as unknown as DocumentSummary,
                              );
                              setShowReprocessDialog(true);
                            }}
                          >
                            <RefreshCw className="h-4 w-4 mr-2" />
                            Reprocess
                          </DropdownMenuItem>
                        )}
                        {canEditDocuments && objCanEdit(document.document_id) && (
                          <DropdownMenuItem
                            onClick={() => handleEnrich(document as unknown as DocumentSummary)}
                            disabled={enriching}
                            title="Enrich with AI-generated categories and labels"
                          >
                            <Sparkles className="h-4 w-4 mr-2" />
                            Enrich
                          </DropdownMenuItem>
                        )}
                        {canManageGroups && (
                          <DropdownMenuItem
                            onClick={() => setGroupAccessTarget({ id: document.document_id, name: document.file_name })}
                          >
                            <ShieldCheck className="h-4 w-4 mr-2" />
                            Group Access
                          </DropdownMenuItem>
                        )}
                        {canDeleteDocuments && objCanDelete(document.document_id) && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem
                              onClick={() => {
                                setSelectedDocument(
                                  document as unknown as DocumentSummary,
                                );
                                setShowDeleteDialog(true);
                              }}
                              className="text-destructive focus:text-destructive"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        )}

        {/* Pagination */}
        {pagination.totalCount > 0 && (
          <div className="mt-4 border-t pt-4">
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalCount={pagination.totalCount}
              pageSize={pagination.pageSize}
              pageSizeOptions={pageSizeOptions}
              startIndex={startIndex}
              endIndex={endIndex}
              canGoPrev={canGoPrev}
              canGoNext={canGoNext}
              onPageChange={goToPage}
              onPageSizeChange={setPageSize}
              onFirstPage={firstPage}
              onLastPage={lastPage}
              onPrevPage={prevPage}
              onNextPage={nextPage}
            />
          </div>
        )}
      </div>

      {/* Assign Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={(open) => {
        setShowCategoryDialog(open);
        if (!open) {
          setCategorySearch("");
          setCategoryPage(1);
        }
      }}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Assign Categories</DialogTitle>
            <DialogDescription>
              Select categories to assign to {selectedIds.size} document
              {selectedIds.size !== 1 ? "s" : ""}.
            </DialogDescription>
          </DialogHeader>
          <SearchInput
            value={categorySearch}
            onChange={(e) => setCategorySearch(e.target.value)}
            onClear={() => setCategorySearch("")}
            placeholder="Search categories..."
            containerClassName="w-full"
          />
          <div className="space-y-1 max-h-[300px] overflow-y-auto min-h-[100px]">
            {categoryLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : categoryResults.items.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {categorySearch
                  ? "No categories match your search."
                  : "No categories found. Create categories first."}
              </p>
            ) : (
              categoryResults.items.map((cat) => (
                <label
                  key={cat.id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedCategoryIds.has(cat.id)}
                    onCheckedChange={() => toggleCategorySelection(cat.id)}
                  />
                  <span className="text-sm">
                    {cat.path ? (
                      <span className="text-muted-foreground">{cat.path} </span>
                    ) : null}
                    {cat.name}
                  </span>
                </label>
              ))
            )}
          </div>
          {categoryResults.total > CATEGORY_PAGE_SIZE && (
            <div className="flex items-center justify-between text-sm text-muted-foreground border-t pt-3">
              <span>
                {categoryResults.total} categor{categoryResults.total !== 1 ? "ies" : "y"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCategoryPage((p) => Math.max(1, p - 1))}
                  disabled={categoryPage <= 1}
                >
                  Previous
                </Button>
                <span>
                  {categoryPage} / {Math.ceil(categoryResults.total / CATEGORY_PAGE_SIZE)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCategoryPage((p) => p + 1)}
                  disabled={categoryPage >= Math.ceil(categoryResults.total / CATEGORY_PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
          {selectedCategoryIds.size > 0 && (
            <div className="text-sm text-muted-foreground">
              {selectedCategoryIds.size} categor{selectedCategoryIds.size !== 1 ? "ies" : "y"} selected
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowCategoryDialog(false);
                setSelectedCategoryIds(new Set());
                setCategorySearch("");
                setCategoryPage(1);
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkAssignCategory}
              disabled={selectedCategoryIds.size === 0 || assigning}
            >
              {assigning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <FolderTree className="h-4 w-4 mr-2" />
                  Assign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Label Dialog */}
      <Dialog open={showLabelDialog} onOpenChange={(open) => {
        setShowLabelDialog(open);
        if (!open) setSelectedLabelIds(new Set());
      }}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Assign Labels</DialogTitle>
            <DialogDescription>
              Select labels to assign to {selectedIds.size} document
              {selectedIds.size !== 1 ? "s" : ""}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1 max-h-[300px] overflow-y-auto min-h-[100px]">
            {labelsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              </div>
            ) : availableLabels.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                No labels found. Create labels first.
              </p>
            ) : (
              availableLabels.map((label) => (
                <label
                  key={label.id}
                  className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                >
                  <Checkbox
                    checked={selectedLabelIds.has(label.id)}
                    onCheckedChange={() => toggleLabelSelection(label.id)}
                  />
                  <div className="flex items-center gap-1.5">
                    {label.color && (
                      <div
                        className="w-2.5 h-2.5 rounded-full"
                        style={{ backgroundColor: label.color }}
                      />
                    )}
                    {!label.color && <Tag className="h-3 w-3 text-muted-foreground" />}
                    <span className="text-sm">{label.name}</span>
                  </div>
                </label>
              ))
            )}
          </div>
          {selectedLabelIds.size > 0 && (
            <div className="text-sm text-muted-foreground">
              {selectedLabelIds.size} label{selectedLabelIds.size !== 1 ? "s" : ""} selected
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowLabelDialog(false);
                setSelectedLabelIds(new Set());
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkAssignLabels}
              disabled={selectedLabelIds.size === 0 || assigningLabels}
            >
              {assigningLabels ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Assigning...
                </>
              ) : (
                <>
                  <Tag className="h-4 w-4 mr-2" />
                  Assign
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reprocess Confirmation Dialog */}
      <Dialog open={showReprocessDialog} onOpenChange={setShowReprocessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprocess Document</DialogTitle>
            <DialogDescription asChild>
              <div>
                <p>
                  Are you sure you want to reprocess "
                  {selectedDocument?.file_name}"? This will:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>
                    Delete existing semantic chunks and embeddings from
                    knowledgebase database
                  </li>
                  <li>Re-extract content using latest models</li>
                  <li>Generate new chunks with updated parsers</li>
                  <li>Create new embeddings and save to database</li>
                </ul>
                <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500 rounded-md flex items-start gap-2">
                  <RefreshCw className="h-4 w-4 text-blue-500 mt-0.5" />
                  <span className="text-sm text-blue-500">
                    The document will be marked as "processing" during
                    reprocessing.
                  </span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowReprocessDialog(false);
                setSelectedDocument(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleReprocess} disabled={reprocessing}>
              {reprocessing ? "Starting..." : "Reprocess Document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Reprocess Confirmation Dialog */}
      <Dialog open={showBulkReprocessDialog} onOpenChange={setShowBulkReprocessDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reprocess {selectedIds.size} Document{selectedIds.size !== 1 ? "s" : ""}</DialogTitle>
            <DialogDescription asChild>
              <div>
                <p>This will reprocess all selected documents:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Delete existing chunks and embeddings</li>
                  <li>Re-extract content using latest models</li>
                  <li>Generate new embeddings</li>
                </ul>
                <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500 rounded-md flex items-start gap-2">
                  <RefreshCw className="h-4 w-4 text-blue-500 mt-0.5" />
                  <span className="text-sm text-blue-500">
                    Documents will be marked as "processing" during reprocessing.
                  </span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkReprocessDialog(false)}
            >
              Cancel
            </Button>
            <Button onClick={handleBulkReprocess}>
              Reprocess {selectedIds.size} Document{selectedIds.size !== 1 ? "s" : ""}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document from knowledgebase</DialogTitle>
            <DialogDescription asChild>
              <div>
                <p>
                  Are you sure you want to delete "{selectedDocument?.file_name}
                  "? This will:
                </p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>
                    Delete all semantic chunks and embeddings from knowledgebase
                  </li>
                  <li>Delete the file from S3 storage</li>
                  <li>Delete the document record from Control Plane</li>
                </ul>
                <div className="mt-4 p-3 bg-red-500/10 border border-red-500 rounded-md flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
                  <span className="text-sm text-red-500">
                    This action cannot be undone!
                  </span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setSelectedDocument(null);
              }}
            >
              Cancel
            </Button>
            <Button
              className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete Permanently"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Group Access Dialog (single document) */}
      {groupAccessTarget && (
        <ObjectGroupAccessDialog
          serviceName="rag"
          objectType="document"
          objectId={groupAccessTarget.id}
          objectName={groupAccessTarget.name}
          open={!!groupAccessTarget}
          onOpenChange={(open) => { if (!open) { setGroupAccessTarget(null); fetchDocPermissions(); } }}
        />
      )}

      {/* Bulk Group Access Dialog */}
      <BulkGroupAccessDialog
        serviceName="rag"
        objectType="document"
        items={Array.from(selectedIds).map((id) => {
          const doc = documents.find((d) => d.document_id === id);
          return { id, name: doc?.file_name ?? id };
        })}
        open={bulkGroupAccessOpen}
        onOpenChange={setBulkGroupAccessOpen}
        onComplete={() => { setSelectedIds(new Set()); fetchDocPermissions(); }}
      />
    </div>
  );
}

const MAX_VISIBLE_BADGES = 2;

function GroupBadges({ permissions }: { permissions?: { group_name: string; role: string }[] }) {
  if (!permissions || permissions.length === 0) {
    return <span className="text-xs text-muted-foreground">&mdash;</span>;
  }

  const visible = permissions.slice(0, MAX_VISIBLE_BADGES);
  const remaining = permissions.length - MAX_VISIBLE_BADGES;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex flex-wrap gap-1">
        {visible.map((p, i) => (
          <Tooltip key={i}>
            <TooltipTrigger asChild>
              <Badge variant="outline" className="text-xs cursor-default">
                {p.group_name}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>{p.role}</TooltipContent>
          </Tooltip>
        ))}
        {remaining > 0 && (
          <Tooltip>
            <TooltipTrigger asChild>
              <Badge variant="secondary" className="text-xs cursor-default">
                +{remaining}
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {permissions.slice(MAX_VISIBLE_BADGES).map((p) => `${p.group_name} (${p.role})`).join(", ")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
